// Shared helpers for the Box Score / Plays tabs: turning sabertooth output + our roster
// data into display strings. Player ids in sabertooth play templates are replaced with names
// from the merged home+opponent roster map.
import type {
  SaberAtBat,
  SaberChildEvent,
  SaberGameSnapshot,
  SaberStatLine,
  SaberTeamStats,
  SaberBoxscoreSituation,
} from "src/utils/sabertooth-client";

export type RosterPlayer = { id?: string; first_name?: string; last_name?: string; number?: string | number };
export type PlayerMap = Record<string, RosterPlayer>;

export function playerNumber(p?: RosterPlayer): string {
  const n = p?.number;
  if (n === undefined || n === null) return "";
  const s = `${n}`.trim();
  return s === "" || s === "NaN" ? "" : `#${s}`;
}

export function playerFullName(p?: RosterPlayer): string {
  if (!p) return "Unknown";
  const name = [p.first_name, p.last_name].filter(Boolean).join(" ").trim();
  return name || playerNumber(p) || "Unknown";
}

/** Replaces ${playerId} placeholders in a sabertooth template with full player names. */
export function renderTemplate(template: string, players: PlayerMap): string {
  if (!template) return "";
  return template.replace(/\$\{([^}]+)\}/g, (_m, id: string) => playerFullName(players[id]));
}

/**
 * Local wall-clock time the at-bat completed, taken from its last pitch's `createdAt` (epoch ms),
 * e.g. "7:04 PM". Uses the browser's local timezone. Empty when the at-bat has no pitches.
 */
export function atBatTime(atBat: SaberAtBat): string {
  const pitches = atBat.pitches ?? [];
  const ms = pitches[pitches.length - 1]?.createdAt;
  if (!ms) return "";
  return new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

/**
 * Earliest and latest pitch times (epoch ms) across the whole game — the first pitch (when play
 * actually started, NOT the scheduled first-pitch) and the last pitch. Scans completed at-bats plus
 * the in-progress one. Either is null when the game has no pitches yet. Powers the header game clock.
 */
export function gamePitchBounds(snap: SaberGameSnapshot): { firstMs: number | null; lastMs: number | null } {
  let firstMs: number | null = null;
  let lastMs: number | null = null;
  const pbp = snap.fullPlayByPlay;
  const atBats = pbp.currentAtBat ? [...pbp.completedAtBats, pbp.currentAtBat] : pbp.completedAtBats;
  for (const ab of atBats) {
    for (const p of ab.pitches ?? []) {
      const ms = p.createdAt;
      if (!ms) continue;
      if (firstMs === null || ms < firstMs) firstMs = ms;
      if (lastMs === null || ms > lastMs) lastMs = ms;
    }
  }
  return { firstMs, lastMs };
}

// A full-defense declaration (a side first taking the field) arrives as this many "fill_position"
// child events at one pitch index; a real in-game substitution is only a handful. We suppress the
// former (GameChanger doesn't log it) while still showing the latter (e.g. "…in at pitcher").
const FULL_DEFENSE_FILL_COUNT = 8;

/**
 * Whether a between-pitch child event belongs in the play log. Baserunning and courtesy-runner
 * events always do. A "fill_position" substitution does too, except the routine full-defense
 * declaration — detected by how many position fills share this event's pitch index.
 */
function shouldRenderChildEvent(c: SaberChildEvent, fillPositionCountByIndex: Map<number, number>): boolean {
  if (!c.template) return false;
  if (c.code === "base_running" || c.code === "replace_runner") return true;
  if (c.code === "fill_position") {
    return (fillPositionCountByIndex.get(c.beforePitchIndex) ?? 0) < FULL_DEFENSE_FILL_COUNT;
  }
  return false;
}

/**
 * The interleaved play-log sequence for an at-bat, matching GameChanger: pitch calls with the
 * between-pitch events (wild pitches, steals, caught stealing, runners scoring/advancing mid-count,
 * substitutions) spliced in before the pitch they occurred on. Each child event replays before the
 * pitch at its `beforePitchIndex`; events after the last pitch trail the sequence.
 *
 * e.g. "…in at pitcher, Strike 1 looking, Ball 1, X scores on wild pitch, X advances to 3rd on the
 * same pitch, Strike 2 swinging, X caught stealing home, catcher X, In play."
 */
export function pitchSequenceText(atBat: SaberAtBat, players: PlayerMap): string {
  const pitches = atBat.pitches ?? [];
  const childEvents = atBat.childEvents ?? [];

  const fillPositionCountByIndex = new Map<number, number>();
  for (const c of childEvents) {
    if (c.code === "fill_position") {
      fillPositionCountByIndex.set(c.beforePitchIndex, (fillPositionCountByIndex.get(c.beforePitchIndex) ?? 0) + 1);
    }
  }

  const segments: string[] = [];
  // Child events keep their recorded order within a pitch index (e.g. the scorer, then each runner
  // advancing on the same wild pitch), so iterate them in-array while filtering to this index.
  const emitChildEventsAt = (matches: (idx: number) => boolean) => {
    for (const c of childEvents) {
      if (matches(c.beforePitchIndex) && shouldRenderChildEvent(c, fillPositionCountByIndex)) {
        segments.push(renderTemplate(c.template, players));
      }
    }
  };
  for (let i = 0; i < pitches.length; i++) {
    emitChildEventsAt((idx) => idx === i);
    if (pitches[i].template) segments.push(pitches[i].template);
  }
  // Anything scheduled at/after the final pitch (a walk-off steal, an end-of-at-bat sub, …).
  emitChildEventsAt((idx) => idx >= pitches.length);

  // Collapse consecutive identical lines (GameChanger records e.g. repeated "Pickoff attempt at 1st").
  const deduped = segments.filter((s, i) => i === 0 || s !== segments[i - 1]);
  return deduped.length ? `${deduped.join(", ")}.` : "";
}

/**
 * Outs recorded during an at-bat, counted as the distinct players retired across both the final-play
 * participants and the between-pitch child events (a caught-stealing or picked-off runner shows up
 * only in a child event, and can be a different player than the batter). Deduped by id because a
 * runner retired to end the at-bat can appear in both lists — counting the set avoids double-count.
 */
export function outsRecordedIn(atBat: SaberAtBat): number {
  const ids = new Set<string>();
  for (const p of atBat.participants ?? []) if (p.out && p.playerId) ids.add(p.playerId);
  for (const c of atBat.childEvents ?? []) for (const p of c.participants ?? []) if (p.out && p.playerId) ids.add(p.playerId);
  return ids.size;
}

/** "Jaxen Maubach singles ..., Noah Rogers scores, B C advances to 3rd." */
export function atBatResultText(atBat: SaberAtBat, players: PlayerMap): string {
  const parts = [renderTemplate(atBat.template, players)];
  for (const part of atBat.participants ?? []) {
    if (part.role === "runner" && part.template) {
      parts.push(renderTemplate(part.template, players));
    }
  }
  return `${parts.filter(Boolean).join(", ").replace(/\.+$/, "")}.`;
}

/** Innings pitched shown the baseball way: whole innings + remaining outs (e.g. 13 outs -> "4.1"). */
export function formatIP(outs: number): string {
  const safe = Number.isFinite(outs) ? outs : 0;
  return `${Math.floor(safe / 3)}.${safe % 3}`;
}

/** Maps a GC age group to the sabertooth bucket (only affects FIP / illegal-pitch config). */
export function toSaberAgeGroup(ageGroup?: string): string {
  if (!ageGroup) return "youth";
  const a = ageGroup.toLowerCase();
  if (a === "18o" || a === "over 18" || a === "adult" || a === "18u") return "adult";
  if (a === "college") return "college";
  if (a.startsWith("high_") || a === "between 13 - 18" || a === "middle_13o") return "prep";
  return "youth";
}

/** Safe numeric stat read (sabertooth stat dicts omit zero-ish keys for some players). */
export const stat = (line: SaberStatLine | undefined, key: string): number =>
  line && Number.isFinite(line[key]) ? line[key] : 0;

/** Player's fielded positions for a team, e.g. ["P","SS"] -> "P, SS". */
export function playerPositions(situation: SaberBoxscoreSituation, id: string): string {
  return (situation.fielderHistory?.[id] ?? []).join(", ");
}

/**
 * Players (with counts) who recorded a given stat, for the box-score note lines:
 * `SB: Keagan Puckett 2, Cooper Hires`. Count is omitted when it is 1.
 */
export function statContributors(
  teamStats: SaberTeamStats,
  players: PlayerMap,
  category: "offense" | "defense",
  key: string
): string[] {
  return Object.entries(teamStats.players ?? {})
    .map(([id, p]) => ({ id, n: stat(p.stats?.[category], key) }))
    .filter((x) => x.n > 0)
    .sort((a, b) => b.n - a.n)
    .map((x) => `${playerFullName(players[x.id])}${x.n > 1 ? ` ${x.n}` : ""}`);
}

export const ordinalInning = (n: number): string => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

/** Short team code for the running-score line, e.g. "Richland County Raptors 14u" -> "RCR". */
export function teamAbbrev(name?: string): string {
  if (!name) return "";
  const words = name
    .replace(/\b\d{1,2}[uU]\b/g, "") // drop age tokens like 14U/10u
    .split(/\s+/)
    .filter((w) => /[a-zA-Z]/.test(w));
  if (words.length === 0) return name.slice(0, 4).toUpperCase();
  if (words.length === 1) return words[0].slice(0, 4).toUpperCase();
  return words
    .map((w) => w[0])
    .join("")
    .slice(0, 4)
    .toUpperCase();
}
